import React, { useState, useEffect } from "react";
import { SaveCancel } from "../../components/PrimaryButton";
import { securePut, secureGet } from "../../config/api";
import { useTranslation } from "react-i18next";
import Input from "../../components/Input";
import SnackbarNotification from '../../components/SnackbarNotification';

import phoneIcon from '../../icons/smartphone.svg';
import imageIcon from '../../icons/imageG.svg';
import videoIcon from '../../icons/video.svg';
import urlIcon from '../../icons/link.svg';
import emailIcon from '../../icons/at-sign.svg';
import zoneIcon from '../../icons/map-pin.svg';
import aIcon from '../../icons/letter.svg';

const FieldForm = ({ onSubmit, onCancel, linkedTo }) => {
  const [formValues, setFormValues] = useState({
    fieldName: "",
    linkedTo: linkedTo || "",
    isRequired: false,
    fieldType: "",
  });

  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  useEffect(() => {
    if (linkedTo) {
      setFormValues(prev => ({ ...prev, linkedTo }));
    }
  }, [linkedTo]);

  useEffect(() => {
    const abortController = new AbortController();
    const fetchTypes = async () => {
      setLoading(true);
      try {
        const response = await secureGet('/config/types', showSnackbar, { signal: abortController.signal });
        setTypes(response.data || []);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Error fetching field types:", error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTypes();
    return () => {
      abortController.abort();
    };
  }, []);

  const handleInputChange = (name, value) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // if (!formValues.fieldName || !formValues.fieldType || !formValues.linkedTo) {
    //   showSnackbar(t('placeholder.empty_fields'), 'error');
    //   return;
    // }

    const moduleMapping = {
      cart: "cart",
      product: "product",
      client: "client",
      brand: "brand",
      family: "family",
      provider: "provider",
      purchase: "purchase",
      types: "types",
    };

    const module = moduleMapping[formValues.linkedTo.toLowerCase()];


    const payload = {
      name: formValues.fieldName,
      type: formValues.fieldType,
      visible: true,
      mandatory: formValues.isRequired,
    };


    try {
      await securePut(`/config/${module}/fields/add`, payload, showSnackbar);

      onSubmit();
    } catch (error) {
      console.error("Error submitting the form:", error);
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const fieldTypeOptions = types.map((type) => ({
    value: type.id,
    label: type.name,
  }));

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[1050]">
      <div className="bg-white px-12 py-8 rounded-[12px] shadow-lg w-full max-w-4xl">
        <h2 className="text-[32px] text-[#38485C] font-semibold mb-4 text-left">
          {t(`form_titles.config`)}
        </h2>
        <form className="space-y-6">
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <Input
                label={t(`fields.config.name`)}
                name="fieldName"
                type="text"
                value={formValues.fieldName}
                onChange={(value) => handleInputChange("fieldName", value)}
                required
              />
              <Input
                label={t(`fields.config.linkedTo`)}
                name="linkedTo"
                type="dropdown"
                options={[
                  { value: "cart", label: "Panier" },
                  { value: "product", label: "Produit" },
                  { value: "client", label: "Client" },
                  { value: "brand", label: "Marque" },
                  { value: "family", label: "Famille" },
                  { value: "provider", label: "Fournisseur" },
                  { value: "purchase", label: "Achat" },
                  { value: "types", label: "Types" },
                ]}
                required
                value={formValues.linkedTo}
                onChange={(value) => handleInputChange("linkedTo", value)}
              />
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isRequired"
                  checked={formValues.isRequired}
                  onChange={(e) => handleInputChange("isRequired", e.target.checked)}
                />
                <label htmlFor="isRequired" className="text-[#38485C]">
                  {t(`rows.required`)}
                </label>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-[16px] font-semibold text-[#ACAFC6]">
              TYPE <span className="text-red-500">*</span>
            </h3>
            {loading ? (
              <p></p>
            ) : (
              <div className="grid grid-cols-5 gap-4">
                {fieldTypeOptions.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    className={`flex items-center justify-center border rounded-[12px] py-3 px-4 transition-colors font-semibold text-[14px] ${formValues.fieldType === type.value
                      ? " text-[#6A78F8] border-opacity-100 border-[#6C78F8]"
                      : "border-[#ACAFC6] text-[#CBCBCD]"
                      }`}
                    onClick={() => handleInputChange("fieldType", type.value)}
                  >
                    <img
                      src={
                        type.label === "PHONE"
                          ? phoneIcon
                          : type.label === "IMAGE"
                            ? imageIcon
                            : type.label === "VIDEO"
                              ? videoIcon
                              : type.label === "URL"
                                ? urlIcon
                                : type.label === "EMAIL"
                                  ? emailIcon
                                  : type.label === "ZONE"
                                    ? zoneIcon
                                    : aIcon
                      }
                      alt={type.label}
                      className="w-5 h-5 mr-2"
                      style={{
                        filter:
                          formValues.fieldType === type.value
                            ? "brightness(0) saturate(100%) invert(36%) sepia(81%) saturate(4000%) hue-rotate(225deg) brightness(95%) contrast(90%)"
                            : "none",
                      }}
                    />
                    {type.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-4">
            <SaveCancel
              onCancelClick={handleCancel}
              onSaveClick={handleSubmit}
            />
          </div>
        </form>
        <SnackbarNotification
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        />
      </div>
    </div>
  );
};

export default FieldForm;
